import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {
    API_CHECK_AUTH_URL,
    API_SETTINGS_ACCESS_OPTIONS,
    API_SETTINGS_ACCESS_PROFILES,
    API_SETTINGS_ACCESS_USERS,
} from '../../service/api.endpoint';
import http from '../../service/axiosConfig';
import './pageAdminSettings.css';

type RefItem = {
    id: string;
    name: string;
    label?: string;
    deposit?: string;
};

type UserOption = {
    id: string;
    username: string;
    name: string;
};

type RoleOption = {
    id: string;
    name: string;
};

type ModuleOption = {
    id: string;
    name: string;
};

type AccessProfile = {
    id: string;
    user: string;
    user_display: string;
    username: string;
    role: string;
    role_display: string;
    orgs: string[];
    org_names: string[];
    deposits: string[];
    deposit_names: string[];
    dns: string[];
    dns_names: string[];
    enabled_modules: string[];
    effective_modules: string[];
    default_modules: string[];
    can_upload_graph: boolean;
    can_manage_access: boolean;
    is_active: boolean;
    comment: string;
};

type OptionsPayload = {
    roles: RoleOption[];
    users: UserOption[];
    orgs: RefItem[];
    deposits: RefItem[];
    dns: RefItem[];
    modules: ModuleOption[];
    role_default_modules: Record<string, string[]>;
};

type SettingsSection = 'accounts' | 'access' | 'modules';

const SETTINGS_MENU: Array<{ id: SettingsSection; title: string; description: string }> = [
    {
        id: 'accounts',
        title: 'Создание аккаунта',
        description: 'Логин, пароль и данные пользователя',
    },
    {
        id: 'access',
        title: 'Предоставление доступов',
        description: 'Роли, подрядчики, месторождения и ДНС',
    },
    {
        id: 'modules',
        title: 'Индивидуальная настройка',
        description: 'Доступные модули для конкретного аккаунта',
    },
];

const emptyOptions: OptionsPayload = {
    roles: [],
    users: [],
    orgs: [],
    deposits: [],
    dns: [],
    modules: [],
    role_default_modules: {},
};

const emptyForm = {
    user: '',
    role: 'CONTRACTOR',
    orgs: [] as string[],
    deposits: [] as string[],
    dns: [] as string[],
    enabled_modules: [] as string[],
    can_upload_graph: false,
    can_manage_access: false,
    comment: '',
};

const emptyUserForm = {
    username: '',
    password: '',
    first_name: '',
    last_name: '',
    email: '',
};

const hasAccessManagement = (access: any) => {
    if (access?.role === 'CONTRACTOR') {
        return false;
    }
    return Boolean(access?.is_admin) || access?.role === 'ADMIN' || Boolean(access?.can_manage_access);
};

const LOCATION_SCOPE_ROLES = ['MASTER_DNS', 'OSF', 'CLUSTER'];

const isContractorRole = (role: string) => role === 'CONTRACTOR';

const usesLocationScope = (role: string) => LOCATION_SCOPE_ROLES.includes(role);

const getAllowedModulesForRole = (role: string, options: OptionsPayload) => (
    options.role_default_modules[role] || []
);

const sanitizeModulesForRole = (role: string, modules: string[], options: OptionsPayload) => {
    const allowedModules = getAllowedModulesForRole(role, options);
    return modules.filter((moduleId) => allowedModules.includes(moduleId));
};

const sanitizeAccessFormForRole = (formValue: typeof emptyForm, options: OptionsPayload) => ({
    ...formValue,
    orgs: isContractorRole(formValue.role) ? formValue.orgs : [],
    deposits: usesLocationScope(formValue.role) ? formValue.deposits : [],
    dns: usesLocationScope(formValue.role) ? formValue.dns : [],
    can_manage_access: isContractorRole(formValue.role) ? false : formValue.can_manage_access,
    enabled_modules: sanitizeModulesForRole(
        formValue.role,
        formValue.enabled_modules.length
            ? formValue.enabled_modules
            : getAllowedModulesForRole(formValue.role, options),
        options,
    ),
});

function PageAdminSettings() {
    const [activeSection, setActiveSection] = useState<SettingsSection>('accounts');
    const [options, setOptions] = useState<OptionsPayload>(emptyOptions);
    const [profiles, setProfiles] = useState<AccessProfile[]>([]);
    const [form, setForm] = useState(emptyForm);
    const [userForm, setUserForm] = useState(emptyUserForm);
    const [moduleProfileId, setModuleProfileId] = useState('');
    const [moduleSelection, setModuleSelection] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const canOpenFromStorage = useMemo(() => {
        try {
            const raw = localStorage.getItem('access');
            const access = raw ? JSON.parse(raw) : {};
            return hasAccessManagement(access);
        } catch {
            return false;
        }
    }, []);
    const [canOpen, setCanOpen] = useState(canOpenFromStorage);

    const getProfileModules = useCallback((profile: AccessProfile | undefined) => {
        if (!profile) return [];
        if (profile.enabled_modules?.length) return sanitizeModulesForRole(profile.role, profile.enabled_modules, options);
        if (profile.effective_modules?.length) return sanitizeModulesForRole(profile.role, profile.effective_modules, options);
        return options.role_default_modules[profile.role] || [];
    }, [options]);

    const selectedModuleProfile = useMemo(
        () => profiles.find((item) => item.id === moduleProfileId),
        [moduleProfileId, profiles],
    );
    const selectedAccessProfile = useMemo(
        () => profiles.find((item) => item.user === form.user),
        [form.user, profiles],
    );
    const accessStats = useMemo(() => {
        const activeProfiles = profiles.filter((item) => item.is_active);
        return {
            users: options.users.length,
            profiles: activeProfiles.length,
            admins: activeProfiles.filter((item) => item.role === 'ADMIN').length,
            contractors: activeProfiles.filter((item) => item.role === 'CONTRACTOR').length,
            scoped: activeProfiles.filter((item) => item.role !== 'ADMIN' && item.role !== 'CONTRACTOR').length,
        };
    }, [options.users.length, profiles]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const userResponse = await http.get(API_CHECK_AUTH_URL);
            const access = userResponse.data?.data?.access || {};
            localStorage.setItem('access', JSON.stringify(access));
            if (!hasAccessManagement(access)) {
                setCanOpen(false);
                setMessage({type: 'error', text: 'Настройки доступа недоступны текущему пользователю'});
                return;
            }
            setCanOpen(true);

            const [optionsResponse, profilesResponse] = await Promise.all([
                http.get(API_SETTINGS_ACCESS_OPTIONS),
                http.get(API_SETTINGS_ACCESS_PROFILES),
            ]);
            setOptions({...emptyOptions, ...(optionsResponse.data || {})});
            setProfiles(profilesResponse.data?.results || []);
        } catch (error: any) {
            setMessage({type: 'error', text: error.response?.data?.detail || 'Не удалось загрузить настройки доступа'});
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = 'АСПО.Контроль | Настройки доступа';
        if (canOpen) {
            void loadData();
        }
    }, [canOpen, loadData]);

    useEffect(() => {
        if (!profiles.length) {
            setModuleProfileId('');
            setModuleSelection([]);
            return;
        }

        const current = profiles.find((item) => item.id === moduleProfileId) || profiles[0];
        if (current.id !== moduleProfileId) {
            setModuleProfileId(current.id);
        }
        setModuleSelection(getProfileModules(current));
    }, [getProfileModules, moduleProfileId, profiles]);

    const handleMultiChange = (field: 'orgs' | 'deposits' | 'dns', values: HTMLCollectionOf<HTMLOptionElement>) => {
        setForm({
            ...form,
            [field]: Array.from(values).filter((item) => item.selected).map((item) => item.value),
        });
    };

    const handleRoleChange = (role: string) => {
        setForm((current) => ({
            ...current,
            role,
            orgs: isContractorRole(role) ? current.orgs : [],
            deposits: usesLocationScope(role) ? current.deposits : [],
            dns: usesLocationScope(role) ? current.dns : [],
            can_manage_access: isContractorRole(role) ? false : current.can_manage_access,
            enabled_modules: getAllowedModulesForRole(role, options),
        }));
    };

    const openAccessProfile = (profile: AccessProfile) => {
        setForm({
            user: profile.user,
            role: profile.role,
            orgs: profile.orgs || [],
            deposits: profile.deposits || [],
            dns: profile.dns || [],
            enabled_modules: getProfileModules(profile),
            can_upload_graph: profile.can_upload_graph,
            can_manage_access: profile.can_manage_access,
            comment: profile.comment || '',
        });
        setActiveSection('access');
    };

    const openModuleProfile = (profile: AccessProfile) => {
        setModuleProfileId(profile.id);
        setModuleSelection(getProfileModules(profile));
        setActiveSection('modules');
    };

    const handleAccessUserChange = (userId: string) => {
        const profile = profiles.find((item) => item.user === userId);
        if (profile) {
            openAccessProfile(profile);
            return;
        }
        setForm({
            ...emptyForm,
            user: userId,
            enabled_modules: options.role_default_modules[emptyForm.role] || [],
        });
    };

    const submitProfile = async (event: React.FormEvent) => {
        event.preventDefault();
        setLoading(true);
        try {
            const payload = {
                ...sanitizeAccessFormForRole(form, options),
            };
            if (selectedAccessProfile) {
                await http.patch(
                    `${API_SETTINGS_ACCESS_PROFILES}${selectedAccessProfile.id}/`,
                    payload,
                );
                setMessage({type: 'success', text: 'Доступы обновлены'});
            } else {
                await http.post(API_SETTINGS_ACCESS_PROFILES, payload);
                setMessage({type: 'success', text: 'Профиль доступа создан'});
            }
            setForm({...emptyForm, enabled_modules: options.role_default_modules[emptyForm.role] || []});
            await loadData();
            setActiveSection('modules');
        } catch (error: any) {
            setMessage({type: 'error', text: error.response?.data?.detail || 'Не удалось сохранить профиль'});
        } finally {
            setLoading(false);
        }
    };

    const submitUser = async (event: React.FormEvent) => {
        event.preventDefault();
        setLoading(true);
        try {
            const response = await http.post(API_SETTINGS_ACCESS_USERS, userForm);
            setUserForm(emptyUserForm);
            setMessage({type: 'success', text: 'Аккаунт создан. Теперь назначьте ему профиль доступа'});
            await loadData();
            setForm((current) => ({...current, user: response.data?.id || ''}));
            setActiveSection('access');
        } catch (error: any) {
            const data = error.response?.data || {};
            const text = data.detail || data.username?.[0] || data.password?.[0] || 'Не удалось создать аккаунт';
            setMessage({type: 'error', text});
        } finally {
            setLoading(false);
        }
    };

    const saveProfileModules = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!selectedModuleProfile) return;

        setLoading(true);
        try {
            await http.patch(
                `${API_SETTINGS_ACCESS_PROFILES}${selectedModuleProfile.id}/`,
                {enabled_modules: sanitizeModulesForRole(selectedModuleProfile.role, moduleSelection, options)},
            );
            setMessage({type: 'success', text: 'Индивидуальные модули сохранены'});
            await loadData();
        } catch (error: any) {
            setMessage({type: 'error', text: error.response?.data?.detail || 'Не удалось сохранить модули'});
        } finally {
            setLoading(false);
        }
    };

    const deactivateProfile = async (profile: AccessProfile) => {
        if (!window.confirm(`Отключить профиль ${profile.user_display}?`)) return;
        setLoading(true);
        try {
            await http.delete(`${API_SETTINGS_ACCESS_PROFILES}${profile.id}/`);
            setMessage({type: 'success', text: 'Профиль отключен'});
            await loadData();
        } catch (error: any) {
            setMessage({type: 'error', text: error.response?.data?.detail || 'Не удалось отключить профиль'});
        } finally {
            setLoading(false);
        }
    };

    const toggleModule = (moduleId: string) => {
        if (selectedModuleProfile && !getAllowedModulesForRole(selectedModuleProfile.role, options).includes(moduleId)) {
            return;
        }
        setModuleSelection((current) => (
            current.includes(moduleId)
                ? current.filter((item) => item !== moduleId)
                : [...current, moduleId]
        ));
    };

    const renderProfilesTable = () => (
        <section className="adminSettings__panel adminSettings__profiles">
            <h2>Профили доступа</h2>
            <div className="adminSettings__tableWrap">
                <table>
                    <thead>
                    <tr>
                        <th>Пользователь</th>
                        <th>Роль</th>
                        <th>Подрядчики</th>
                        <th>Месторождения</th>
                        <th>ДНС</th>
                        <th>Модули</th>
                        <th>Права</th>
                        <th>Статус</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    {profiles.map((item) => (
                        <tr key={item.id} className={!item.is_active ? 'adminSettings__inactive' : ''}>
                            <td>
                                <strong>{item.user_display}</strong>
                                <span>{item.username}</span>
                            </td>
                            <td>{item.role_display}</td>
                            <td>{formatList(item.org_names)}</td>
                            <td>{formatList(item.deposit_names)}</td>
                            <td>{formatList(item.dns_names)}</td>
                            <td>{formatModuleList(item.effective_modules, options.modules)}</td>
                            <td>
                                {item.can_upload_graph && <span className="adminSettings__tag">График</span>}
                                {item.can_manage_access && <span className="adminSettings__tag">Доступы</span>}
                            </td>
                            <td>{item.is_active ? 'Активен' : 'Отключен'}</td>
                            <td>
                                <div className="adminSettings__rowActions">
                                    <button type="button" onClick={() => openAccessProfile(item)} disabled={loading}>
                                        Доступы
                                    </button>
                                    <button type="button" onClick={() => openModuleProfile(item)} disabled={loading}>
                                        Модули
                                    </button>
                                {item.is_active && (
                                    <button type="button" onClick={() => deactivateProfile(item)} disabled={loading}>
                                        Отключить
                                    </button>
                                )}
                                </div>
                            </td>
                        </tr>
                    ))}
                    {!profiles.length && (
                        <tr>
                            <td colSpan={9}>Профилей доступа пока нет</td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>
        </section>
    );

    if (!canOpen) {
        return (
            <main className="adminSettings">
                <section className="adminSettings__denied">Настройки доступа недоступны текущему пользователю</section>
            </main>
        );
    }

    return (
        <main className="adminSettings">
            <header className="adminSettings__header">
                <div>
                    <span className="adminSettings__eyebrow">Модуль авторизации</span>
                    <h1>Настройки доступа</h1>
                    <p>Аккаунты, области видимости и индивидуальные права пользователей</p>
                </div>
                <button type="button" onClick={loadData} disabled={loading}>
                    {loading ? 'Обновляем...' : 'Обновить'}
                </button>
            </header>

            <section className="adminSettings__summary" aria-label="Сводка по доступам">
                <div>
                    <span>Пользователей</span>
                    <strong>{accessStats.users}</strong>
                </div>
                <div>
                    <span>Активных профилей</span>
                    <strong>{accessStats.profiles}</strong>
                </div>
                <div>
                    <span>Админов</span>
                    <strong>{accessStats.admins}</strong>
                </div>
                <div>
                    <span>Подрядчиков</span>
                    <strong>{accessStats.contractors}</strong>
                </div>
                <div>
                    <span>ДНС / ОСФ / Кластер</span>
                    <strong>{accessStats.scoped}</strong>
                </div>
            </section>

            {message && (
                <div className={`adminSettings__message adminSettings__message_${message.type}`}>
                    <span>{message.text}</span>
                    <button type="button" onClick={() => setMessage(null)}>x</button>
                </div>
            )}

            <section className="adminSettings__workspace">
                <aside className="adminSettings__nav" aria-label="Разделы настроек">
                    <div className="adminSettings__navTitle">Разделы</div>
                    {SETTINGS_MENU.map((item, index) => (
                        <button
                            key={item.id}
                            type="button"
                            className={activeSection === item.id ? 'adminSettings__navItem adminSettings__navItem_active' : 'adminSettings__navItem'}
                            onClick={() => setActiveSection(item.id)}
                        >
                            <span className="adminSettings__navNumber">{index + 1}</span>
                            <span>
                                <strong>{item.title}</strong>
                                <small>{item.description}</small>
                            </span>
                        </button>
                    ))}
                </aside>

                <div className="adminSettings__content">
                    {activeSection === 'accounts' && (
                        <section className="adminSettings__layout adminSettings__layout_single">
                            <form className="adminSettings__panel adminSettings__form" onSubmit={submitUser}>
                                <h2>Создание аккаунта</h2>

                                <label>
                                    Логин
                                    <input
                                        required
                                        value={userForm.username}
                                        onChange={(event) => setUserForm({...userForm, username: event.target.value})}
                                    />
                                </label>

                                <label>
                                    Пароль
                                    <input
                                        required
                                        type="password"
                                        autoComplete="new-password"
                                        value={userForm.password}
                                        onChange={(event) => setUserForm({...userForm, password: event.target.value})}
                                    />
                                </label>

                                <label>
                                    Имя
                                    <input
                                        value={userForm.first_name}
                                        onChange={(event) => setUserForm({...userForm, first_name: event.target.value})}
                                    />
                                </label>

                                <label>
                                    Компания / фамилия
                                    <input
                                        value={userForm.last_name}
                                        onChange={(event) => setUserForm({...userForm, last_name: event.target.value})}
                                    />
                                </label>

                                <label>
                                    Email
                                    <input
                                        type="email"
                                        value={userForm.email}
                                        onChange={(event) => setUserForm({...userForm, email: event.target.value})}
                                    />
                                </label>

                                <button className="adminSettings__primary" type="submit" disabled={loading}>Создать аккаунт</button>
                            </form>
                        </section>
                    )}

                    {activeSection === 'access' && (
                        <section className="adminSettings__layout">
                            <form className="adminSettings__panel adminSettings__form" onSubmit={submitProfile}>
                                <h2>Предоставление доступов</h2>

                                <label>
                                    Пользователь
                                    <select required value={form.user} onChange={(event) => handleAccessUserChange(event.target.value)}>
                                        <option value="">Выберите пользователя</option>
                                        {options.users.map((item) => (
                                            <option key={item.id} value={item.id}>{item.name} / {item.username}</option>
                                        ))}
                                    </select>
                                </label>

                                <label>
                                    Роль
                                    <select required value={form.role} onChange={(event) => handleRoleChange(event.target.value)}>
                                        {options.roles.map((item) => (
                                            <option key={item.id} value={item.id}>{item.name}</option>
                                        ))}
                                    </select>
                                </label>

                                {selectedAccessProfile && (
                                    <div className="adminSettings__profileHint adminSettings__profileHint_notice">
                                        <strong>Профиль уже создан</strong>
                                        <span>Сохранение обновит доступы выбранного пользователя.</span>
                                    </div>
                                )}

                                {isContractorRole(form.role) && (
                                    <label>
                                        Подрядчики
                                        <select multiple value={form.orgs} onChange={(event) => handleMultiChange('orgs', event.target.selectedOptions)}>
                                            {options.orgs.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                                        </select>
                                    </label>
                                )}

                                {usesLocationScope(form.role) && (
                                    <>
                                        <label>
                                            Месторождения
                                            <select multiple value={form.deposits} onChange={(event) => handleMultiChange('deposits', event.target.selectedOptions)}>
                                                {options.deposits.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                                            </select>
                                        </label>

                                        <label>
                                            ДНС
                                            <select multiple value={form.dns} onChange={(event) => handleMultiChange('dns', event.target.selectedOptions)}>
                                                {options.dns.map((item) => <option key={item.id} value={item.id}>{item.label || item.name}</option>)}
                                            </select>
                                        </label>
                                    </>
                                )}

                                <label>
                                    Комментарий
                                    <textarea value={form.comment} onChange={(event) => setForm({...form, comment: event.target.value})}/>
                                </label>

                                <div className="adminSettings__checks">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={form.can_upload_graph}
                                            onChange={(event) => setForm({...form, can_upload_graph: event.target.checked})}
                                        />
                                        Может загружать график
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={form.can_manage_access}
                                            disabled={isContractorRole(form.role)}
                                            onChange={(event) => setForm({...form, can_manage_access: event.target.checked})}
                                        />
                                        Может управлять доступами
                                    </label>
                                </div>

                                <button className="adminSettings__primary" type="submit" disabled={loading}>
                                    {selectedAccessProfile ? 'Сохранить доступы' : 'Создать профиль'}
                                </button>
                            </form>

                            {renderProfilesTable()}
                        </section>
                    )}

                    {activeSection === 'modules' && (
                        <section className="adminSettings__layout">
                            <form className="adminSettings__panel adminSettings__form" onSubmit={saveProfileModules}>
                                <h2>Индивидуальная настройка аккаунта</h2>

                                <label>
                                    Профиль
                                    <select
                                        required
                                        value={moduleProfileId}
                                        onChange={(event) => {
                                            const profile = profiles.find((item) => item.id === event.target.value);
                                            setModuleProfileId(event.target.value);
                                            setModuleSelection(getProfileModules(profile));
                                        }}
                                    >
                                        {profiles.map((item) => (
                                            <option key={item.id} value={item.id}>{item.user_display} / {item.role_display}</option>
                                        ))}
                                    </select>
                                </label>

                                {selectedModuleProfile && (
                                    <div className="adminSettings__profileHint">
                                        <strong>{selectedModuleProfile.username}</strong>
                                        <span>{selectedModuleProfile.role_display}</span>
                                    </div>
                                )}

                                <div className="adminSettings__moduleGrid">
                                    {options.modules
                                        .filter((item) => getAllowedModulesForRole(selectedModuleProfile?.role || '', options).includes(item.id))
                                        .map((item) => (
                                        <label key={item.id} className="adminSettings__moduleLabel">
                                            <input
                                                type="checkbox"
                                                checked={moduleSelection.includes(item.id)}
                                                onChange={() => toggleModule(item.id)}
                                            />
                                            {item.name}
                                        </label>
                                    ))}
                                </div>

                                <button className="adminSettings__primary" type="submit" disabled={loading || !selectedModuleProfile}>
                                    Сохранить модули
                                </button>
                            </form>

                            {renderProfilesTable()}
                        </section>
                    )}
                </div>
            </section>
        </main>
    );
}

function formatList(items: string[]) {
    return items?.length ? items.join(', ') : '—';
}

function formatModuleList(ids: string[], modules: ModuleOption[]) {
    const names = ids
        .map((id) => modules.find((item) => item.id === id)?.name)
        .filter(Boolean);
    return names.length ? names.join(', ') : '—';
}

export default PageAdminSettings;
